import React, { useState, useEffect } from 'react';
import { Card, Button, Select, Row, Col, Typography, Alert, Divider, InputNumber, Form, message} from 'antd';
import { CalculatorOutlined } from '@ant-design/icons';
import api from '../services/api';

const { Text } = Typography;
const { Option } = Select;

const fmtN = (v, d) => { d = d||3; const p = parseFloat(v); return isNaN(p) ? '-' : p.toFixed(d).replace('.', ','); };

const calcEspMinima = (tipo, pmtaKgf, Ri, S, E, D, alpha) => {
  if (!pmtaKgf || !S || !E || !D) return null;
  const P = pmtaKgf;
  switch ((tipo||'').toLowerCase()) {
    case 'semi-elíptico': return (P * D) / (2 * S * E - 0.2 * P);
    case 'semiesférico': return (P * Ri) / (2 * S * E - 0.2 * P);
    case 'plano': return D * Math.sqrt(0.13 * P / S);
    case 'cônico': { const a = (alpha||30)*Math.PI/180; return (P*D)/(2*Math.cos(a)*(S*E-0.6*P)); }
    case 'torisférico': return (0.885 * P * D) / (S * E - 0.1 * P);
    default: return (P * D) / (2 * S * E - 0.2 * P);
  }
};

// A vida remanescente tem duas portas de entrada:
//
//   1. form      caminho do vaso de pressao. O componente deriva a espessura
//                minima dos campos do laudo por UG-27 / UG-32.
//   2. entrada   para geometria em que essas formulas nao valem - camara
//                retangular, por exemplo, onde a espessura minima sai do
//                Apendice 13 e nao tem forma fechada. Quem chama entrega a
//                espessura minima ja calculada e a memoria pronta.
//
// A regra de taxa de corrosao e de vida remanescente e a mesma nas duas.
// Nunca duplicar essa conta: foi divergencia desse tipo que produziu o erro
// de vida remanescente do laudo VP-04.
//
// entrada = {
//   valores: { vr_esp_anterior, vr_ano_medicao_anterior, vr_categoria_id },
//   onChange: (campo, valor) => {},
//   menorEspessura: Number,      // mm medida em campo
//   espessuraMinima: Number,     // mm exigida pela memoria de calculo
//   anoFabricacao: Number,
//   rotuloPressao: String,       // ex.: '2,40 bar'
//   rotuloEquipamento: String,   // ex.: 'Camara'
//   memoria: [String]            // linhas da memoria de espessura minima
// }
function VidaRemanescente({ form, laudoId, dadosRef, entrada }) {
  const [categorias, setCategorias] = useState([]);
  const [resultado, setResultado] = useState(null);

  const ext = !!entrada;
  const vls = ext ? (entrada.valores || {}) : {};
  const rotuloEquip = ext ? (entrada.rotuloEquipamento || 'Equipamento') : 'Vaso';

  const espAnterior = ext ? vls.vr_esp_anterior : (form ? form.getFieldValue('vr_esp_anterior') : null);
  const anoMedicaoAnterior = ext ? vls.vr_ano_medicao_anterior : (form ? form.getFieldValue('vr_ano_medicao_anterior') : null);
  const categoriaSel = ext ? vls.vr_categoria_id : (form ? form.getFieldValue('vr_categoria_id') : null);

  const setCampo = (campo, v) => {
    if (ext) { if (entrada.onChange) entrada.onChange(campo, v); return; }
    if (form) form.setFieldsValue({ [campo]: v });
  };

  useEffect(() => {
    api.get('/corrosao').then(r => setCategorias(r.data)).catch(() => {});
  }, []);

  const calcular = () => {
    const anoAtual = new Date().getFullYear();
    const cat = categorias.find(c => c.id === categoriaSel);
    const taxa = cat ? parseFloat(cat.taxa_corrosao) : 0;

    // ---- de onde vem a espessura minima -------------------------------
    let menorEsp, eMinCorrespondente, anoFab, vaso = null;

    if (ext) {
      menorEsp = parseFloat(entrada.menorEspessura) || 0;
      eMinCorrespondente = parseFloat(entrada.espessuraMinima) || 0;
      anoFab = parseInt(entrada.anoFabricacao) || 0;
      if (!menorEsp) {
        message.warning('Preencha as medições de espessura antes de calcular a vida remanescente.');
        return;
      }
      if (!eMinCorrespondente) {
        message.warning('Rode o cálculo do laudo primeiro: a espessura mínima vem da memória de cálculo.');
        return;
      }
    } else {
      const vals = form ? form.getFieldsValue(true) : {};
      const S = parseFloat(vals.tensao) || 0;
      const Ec = parseFloat(vals.coef_c) || 0.7;
      const Et = parseFloat(vals.coef_t) || 0.7;
      const D = parseFloat(vals.diametro_externo) || 0;
      // A Medida X e a GERATRIZ do cone, nao a altura: h = raiz(X^2 - R^2) e
      // cosa = h/X, como em calculoLaudo.js. E ela e por tampo. Antes isso lia
      // vals.medida_x, que nao existe no formulario, entao o semiangulo caia
      // sempre em 30 graus qualquer que fosse o cone real.
      const alphaDe = (X, Rint) => {
        const x = parseFloat(X);
        if (!(x > 0) || !(Rint > 0) || x <= Rint) return 30;
        return Math.acos(Math.sqrt(x * x - Rint * Rint) / x) * 180 / Math.PI;
      };

      const espCascoArr = [1,2,3,4,5].map(i => parseFloat(vals['esp_casco_'+i])||999).filter(v=>v<999);
      const espTampoArr = [1,2,3,4,5].map(i => parseFloat(vals['esp_tampo_'+i])||999).filter(v=>v<999);
      const espCasco = parseFloat(vals.esp_casco_encontrada) || (espCascoArr.length ? Math.min(...espCascoArr) : 0);
      const espTampo = parseFloat(vals.esp_tampo_encontrada) || (espTampoArr.length ? Math.min(...espTampoArr) : 0);
      // Espessura nao medida entra como 999 na comparacao, senao um campo
      // vazio (0) venceria o minimo e escolheria o e_min do componente errado.
      const cascoCmp = espCasco || 999;
      const tampoCmp = espTampo || 999;
      menorEsp = Math.min(cascoCmp, tampoCmp);
      if (menorEsp === 999) { message.warning('Preencha as espessuras na guia Dimensional!'); return; }

      const pmtaFinalValor = parseFloat(vals.pmta_final_manual) || parseFloat(vals.pmta_final) || 0;
      const pmtaUnidade = vals.pmta_final_manual_unidade || 'kgf';
      const conv = { kgf: 1, psi: 0.070307, bar: 1.01972, mpa: 10.1972 };
      const pmtaKgf = pmtaFinalValor * (conv[pmtaUnidade] || 1);
      if (!pmtaKgf) { message.warning('Informe a PMTA Final na guia Dimensional!'); return; }

      const circunferencial = parseFloat(vals.medida_circunferencial) || 0;
      const D_calc = D > 0 ? D : (circunferencial > 0 ? circunferencial / Math.PI : 0);
      const Re = D_calc / 2;
      const Ri = Re - espCasco;
      const D_int = 2 * Ri;
      const tipoSup = vals.tipo_tampo_sup || 'semi-elíptico';
      const tipoInf = vals.tipo_tampo_inf || 'semi-elíptico';

      const eMinCasco = (pmtaKgf * Ri) / (S * Ec - 0.6 * pmtaKgf);
      const eMinTampoSup = calcEspMinima(tipoSup, pmtaKgf, Ri, S, Et, D_int, alphaDe(vals.medida_x_sup, Ri));
      const eMinTampoInf = calcEspMinima(tipoInf, pmtaKgf, Ri, S, Et, D_int, alphaDe(vals.medida_x_inf, Ri));
      eMinCorrespondente = cascoCmp <= tampoCmp ? eMinCasco : Math.max(eMinTampoSup||0, eMinTampoInf||0);

      anoFab = parseInt(vals.ano_fabricacao) || 0;
      vaso = { espCasco, espTampo, eMinCasco, eMinTampoSup, eMinTampoInf,
               tipoSup, tipoInf, pmtaKgf, S, Ec, Et, D: D_calc, D_int, Ri };
    }

    // ---- conta de vida remanescente, igual nos dois caminhos ----------
    const vrAmbiental = taxa > 0 ? (menorEsp - eMinCorrespondente) / taxa : null;
    const idadeVaso = anoFab > 0 ? anoAtual - anoFab : 0;

    let vrResidual = null;
    let vrResidualMsg = null;
    let taxaReal = null;
    let usouTaxaAmbiental = false;

    if (espAnterior && anoMedicaoAnterior) {
      const anosEntreMedicoes = anoAtual - anoMedicaoAnterior;
      if (anosEntreMedicoes <= 0) {
        vrResidualMsg = 'O ano da medição anterior deve ser menor que o ano atual.';
      } else {
        taxaReal = (parseFloat(espAnterior) - menorEsp) / anosEntreMedicoes;
        if (taxaReal <= 0) {
          vrResidualMsg = 'Taxa de corrosão calculada inválida (espessura anterior menor ou igual à atual).';
        } else {
          vrResidual = (menorEsp - eMinCorrespondente) / taxaReal;
        }
      }
    } else if (!anoFab) {
      vrResidualMsg = rotuloEquip + ' não possui ano de fabricação informado.';
    } else if (idadeVaso <= 5) {
      vrResidualMsg = rotuloEquip + ' possui ' + idadeVaso + ' ano(s) desde a fabricação (mínimo 5 anos necessários).';
    } else {
      taxaReal = taxa;
      usouTaxaAmbiental = true;
      vrResidual = taxa > 0 ? (menorEsp - eMinCorrespondente) / taxa : null;
    }

    setResultado(Object.assign({
      menorEsp, eMinCorrespondente,
      taxa, cat, vrAmbiental, vrResidual, vrResidualMsg, taxaReal,
      idadeVaso, anoFab, espAnterior, anoMedicaoAnterior,
      usouTaxaAmbiental, anoAtual,
      rotuloPressao: ext ? (entrada.rotuloPressao || '-') : null,
      memoria: ext ? (entrada.memoria || []) : null
    }, vaso || {}));

    if (ext && entrada.onResultado) entrada.onResultado({ taxaReal, vrResidual, vrAmbiental });
  };

  // ---- os tres campos de entrada --------------------------------------
  const campoEsp = ext ? (<>
      <Text>Espessura anterior encontrada (mm)</Text>
      <InputNumber style={{ width: '100%' }} precision={3} decimalSeparator="," placeholder="Ex: 4,200"
        value={espAnterior} onChange={(v) => setCampo('vr_esp_anterior', v)} />
    </>) : (
      <Form.Item name="vr_esp_anterior" label="Espessura anterior encontrada (mm)" style={{ marginBottom: 4 }}>
        <InputNumber style={{ width: '100%' }} precision={3} decimalSeparator="," placeholder="Ex: 4,200" />
      </Form.Item>);

  const campoAno = ext ? (<>
      <Text>Ano da medição anterior</Text>
      <InputNumber style={{ width: '100%' }} min={1900} max={2100} placeholder="Ex: 2020"
        value={anoMedicaoAnterior} onChange={(v) => setCampo('vr_ano_medicao_anterior', v)} decimalSeparator="," />
    </>) : (
      <Form.Item name="vr_ano_medicao_anterior" label="Ano da medição anterior" style={{ marginBottom: 0 }}>
        <InputNumber style={{ width: '100%' }} min={1900} max={2100} placeholder="Ex: 2020" decimalSeparator="," />
      </Form.Item>);

  const opcoesCat = categorias.map(c => (
    <Option key={c.id} value={c.id}>{c.codigo} — {c.descricao} ({String(parseFloat(c.taxa_corrosao).toFixed(4)).replace('.', ',')} mm/ano)</Option>
  ));

  const campoCat = ext ? (<>
      <Text>Categoria de Corrosão</Text>
      <Select style={{ width: '100%' }} placeholder="Selecione a categoria"
        value={categoriaSel} onChange={(v) => setCampo('vr_categoria_id', v)}>{opcoesCat}</Select>
    </>) : (
      <Form.Item name="vr_categoria_id" label="Categoria de Corrosão" style={{ marginBottom: 0 }}>
        <Select placeholder="Selecione a categoria">{opcoesCat}</Select>
      </Form.Item>);

  return (
    <Card title="Vida Remanescente" size="small">
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={6}>
          {campoEsp}
          <Text type="secondary" style={{ fontSize: 11 }}>Menor esp. da medição anterior (opcional)</Text>
        </Col>
        <Col span={4}>{campoAno}</Col>
        <Col span={10}>{campoCat}</Col>
        <Col span={4} style={{ paddingTop: 22 }}>
          <Button type="primary" icon={<CalculatorOutlined />} onClick={calcular} style={{ width: '100%', marginTop: 8 }}>Calcular VR</Button>
        </Col>
      </Row>

      {resultado && (<>
        {resultado.memoria ? (<>
          <Divider orientation="left">Espessura Mínima para a PMTA ({resultado.rotuloPressao})</Divider>
          <div style={{ background: '#f9f9f9', padding: 12, borderRadius: 8, marginBottom: 12, fontSize: 11, fontFamily: 'monospace' }}>
            {resultado.memoria.map((linha, i) => <p key={i} style={{ margin: '0 0 4px' }}>{linha}</p>)}
            <p style={{ margin: 0 }}>Menor espessura encontrada: <strong>{fmtN(resultado.menorEsp)} mm</strong> | e_min correspondente: <strong>{fmtN(resultado.eMinCorrespondente)} mm</strong></p>
          </div>
        </>) : (<>
          <Divider orientation="left">Espessuras Mínimas para PMTA Final ({fmtN(resultado.pmtaKgf)} kgf/cm²)</Divider>
          <div style={{ background: '#f9f9f9', padding: 12, borderRadius: 8, marginBottom: 12, fontSize: 11, fontFamily: 'monospace' }}>
            <p><strong>Casco (UG-27):</strong> e_min = (P × Ri) / (S × E - 0,6 × P) = ({fmtN(resultado.pmtaKgf)} × {fmtN(resultado.Ri)}) / ({fmtN(resultado.S)} × {fmtN(resultado.Ec)} - 0,6 × {fmtN(resultado.pmtaKgf)}) = <strong>{fmtN(resultado.eMinCasco)} mm</strong></p>
            <p><strong>Tampo Sup. ({resultado.tipoSup}):</strong> e_min = <strong>{fmtN(resultado.eMinTampoSup)} mm</strong></p>
            <p><strong>Tampo Inf. ({resultado.tipoInf}):</strong> e_min = <strong>{fmtN(resultado.eMinTampoInf)} mm</strong></p>
            <p>Menor espessura encontrada: <strong>{fmtN(resultado.menorEsp)} mm</strong> | e_min correspondente: <strong>{fmtN(resultado.eMinCorrespondente)} mm</strong></p>
          </div>
        </>)}

        <Alert type="info" style={{ marginBottom: 12 }} message={
          <div>
            <Text strong>VR por Enquadramento Ambiental — {resultado.cat?.codigo} ({fmtN(resultado.taxa, 4)} mm/ano)</Text>
            <div style={{ fontFamily: 'monospace', fontSize: 11, margin: '6px 0' }}>
              VR = (e_encontrada - e_min) / taxa_corrosão<br/>
              VR = ({fmtN(resultado.menorEsp)} - {fmtN(resultado.eMinCorrespondente)}) / {fmtN(resultado.taxa, 4)}<br/>
              <strong>VR Ambiental = {resultado.vrAmbiental !== null ? fmtN(resultado.vrAmbiental, 1) + ' anos' : 'N/A'}</strong>
            </div>
          </div>
        } />

        <Alert type={resultado.vrResidualMsg ? 'warning' : 'success'} message={
          <div>
            <Text strong>VR Residual</Text>
            {resultado.vrResidualMsg ? (
              <div style={{ marginTop: 4 }}>
                <Text type="warning">⚠️ {resultado.vrResidualMsg}</Text>
              </div>
            ) : (
              <div style={{ fontFamily: 'monospace', fontSize: 11, margin: '6px 0' }}>
                {resultado.usouTaxaAmbiental ? (
                  <>Sem medição anterior — usando taxa ambiental ({resultado.cat?.codigo}): {fmtN(resultado.taxaReal, 4)} mm/ano<br/></>
                ) : (
                  <>
                    Esp. anterior ({resultado.anoMedicaoAnterior}): {fmtN(resultado.espAnterior)} mm | Esp. atual: {fmtN(resultado.menorEsp)} mm<br/>
                    Anos entre medições: {resultado.anoAtual - resultado.anoMedicaoAnterior}<br/>
                    Taxa real = ({fmtN(resultado.espAnterior)} - {fmtN(resultado.menorEsp)}) / {resultado.anoAtual - resultado.anoMedicaoAnterior} = {fmtN(resultado.taxaReal, 4)} mm/ano<br/>
                  </>
                )}
                VR = ({fmtN(resultado.menorEsp)} - {fmtN(resultado.eMinCorrespondente)}) / {fmtN(resultado.taxaReal, 4)}<br/>
                <strong>VR Residual = {fmtN(resultado.vrResidual, 1)} anos</strong>
              </div>
            )}
          </div>
        } />
      </>)}
    </Card>
  );
}

export default VidaRemanescente;
